import type { AxiosInstance } from 'axios';
import { ServiceAPI } from 'viewup-api/v2';
import { clientTypeHeaders, HttpClient, type Interceptor } from 'viewup-lib/common';
import { ErrorInterceptor } from './errorInterceptor';
import { HeaderInterceptor } from './headerInterceptor';
import { SsrAuthInterceptor } from './ssrAuthInterceptor';
import { TelemetryFirstAuthInterceptor } from './telemetryFirstAuthInterceptor';
import { createClientTelemetryInterceptor } from './telemetryInterceptor';
import { TokenRepositoryImpl } from './tokenRepository';

export const tokenRepository = new TokenRepositoryImpl();
export const authInterceptor = new TelemetryFirstAuthInterceptor(
  tokenRepository,
  '/v2/auth/refresh',
);
export const errorInterceptor = new ErrorInterceptor();
export const headerInterceptor = new HeaderInterceptor();

/**
 * tail(index 4) 슬롯용 telemetry — `responseInterceptorOnRejected`를 **뺀** 형태.
 *
 * 실패 기록은 index 0의 `TelemetryFirstAuthInterceptor`가 소유한다. response는 FIFO라
 * tail이 관측하는 시점엔 Auth/Error 인터셉터가 원본 `AxiosError`를 `.config` 없는 타입으로
 * 갈아치운 뒤다. 여기에 rejected 핸들러를 남겨두면 **중복 기록 + url:'' 쓰레기 breadcrumb**이
 * 생긴다(오귀속 재발). 성공 기록(traceparent 주입 + 200 breadcrumb)만 tail이 담당한다.
 */
const createTailTelemetryInterceptor = (): Interceptor | undefined => {
  const telemetry = createClientTelemetryInterceptor();
  if (!telemetry) return undefined;
  const { responseInterceptorOnRejected: _ownedByAuthSlot, ...withoutFailureRecording } = telemetry;
  return withoutFailureRecording;
};

// --- Client (lazy singleton) ---
let _clientHttpClient: AxiosInstance | null = null;
let _clientServiceApi: ServiceAPI | null = null;

const getClientHttpClient = (): AxiosInstance => {
  if (!_clientHttpClient) {
    _clientHttpClient = HttpClient.getInstance({
      baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
      customConfig: { headers: { ...clientTypeHeaders } },
      interceptors: [
        authInterceptor,
        errorInterceptor,
        // index 2는 DuplicateRequestInterceptor 전용 슬롯(예약, 미사용) — 제거 금지.
        // 런타임은 falsy를 skip하므로(`forEach(i => { if (!i) return; ... })`) 빼도 등록 위치는
        // 안 밀린다. 깨지는 건 타입 튜플이다.
        undefined,
        headerInterceptor,
        // telemetry는 base Interceptor라 index 2(DuplicateRequestInterceptor 전용) 타입에 안 맞는다.
        // rest 구간(index 3+)에만 놓을 수 있다.
        // 활성 판정·kill-switch 의미는 ./telemetryInterceptor 가 소유한다.
        // 실패 기록은 index 0의 TelemetryFirstAuthInterceptor 소유 — 위 헬퍼 주석 참조.
        createTailTelemetryInterceptor(),
      ],
    });
    authInterceptor.attachInstance(_clientHttpClient);
  }
  return _clientHttpClient;
};

const getClientServiceApi = (): ServiceAPI => {
  if (!_clientServiceApi) {
    _clientServiceApi = new ServiceAPI(
      getClientHttpClient(),
      process.env.NEXT_PUBLIC_VIEWUP_API_URL ?? '',
    );
  }
  return _clientServiceApi;
};

// --- SSR (stateless, refresh 없음, 토큰 리셋 없음) ---
let _ssrHttpClient: AxiosInstance | null = null;
let _ssrServiceApi: ServiceAPI | null = null;

const getSsrHttpClient = (): AxiosInstance => {
  if (!_ssrHttpClient) {
    const ssrAuthInterceptor = new SsrAuthInterceptor(tokenRepository, '/v2/auth/refresh');
    const ssrErrorInterceptor = new ErrorInterceptor();
    _ssrHttpClient = HttpClient.createInstance({
      baseURL: process.env.NEXT_PUBLIC_VIEWUP_API_URL,
      customConfig: { headers: { ...clientTypeHeaders } },
      interceptors: [ssrAuthInterceptor, ssrErrorInterceptor],
    });
  }
  return _ssrHttpClient;
};

const getSsrServiceApi = (): ServiceAPI => {
  if (!_ssrServiceApi) {
    _ssrServiceApi = new ServiceAPI(
      getSsrHttpClient(),
      process.env.NEXT_PUBLIC_VIEWUP_API_URL ?? '',
    );
  }
  return _ssrServiceApi;
};

// --- 환경 분기 Proxy ---
const getHttpClient = (): AxiosInstance =>
  typeof window === 'undefined' ? getSsrHttpClient() : getClientHttpClient();

const getServiceApi = (): ServiceAPI =>
  typeof window === 'undefined' ? getSsrServiceApi() : getClientServiceApi();

export const httpClient: AxiosInstance = new Proxy({} as AxiosInstance, {
  get(_target, prop, receiver) {
    return Reflect.get(getHttpClient(), prop, receiver);
  },
});

export const serviceApi: ServiceAPI = new Proxy({} as ServiceAPI, {
  get(_target, prop, receiver) {
    return Reflect.get(getServiceApi(), prop, receiver);
  },
});
